import { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';

import EspelhoReferencialService, {
    IEspelhoReferencial,
    IEspelhoReferencialForm,
} from '../../../../services/EspelhoReferencialService';

// DEVN3-606 — "Configurar referencial": espelho conta contábil → referencial do cliente.
// A planilha (.xls/.xlsx) entra por posição fixa: A = conta, C = nome, D = referencial; só contas
// com referencial são carregadas e o reenvio mescla por conta.

const POR_PAGINA = 50;
const formVazio: IEspelhoReferencialForm = { conta: '', nomeConta: '', referencial: '' };

interface Props {
    onVoltar: () => void;
}

const EspelhoReferencial = ({ onVoltar }: Props) => {
    const [itens, setItens] = useState<IEspelhoReferencial[]>([]);
    const [total, setTotal] = useState(0);
    const [pagina, setPagina] = useState(0);
    const [busca, setBusca] = useState('');
    const [buscaAplicada, setBuscaAplicada] = useState('');
    const [loading, setLoading] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [editando, setEditando] = useState<IEspelhoReferencial | null>(null);
    const [form, setForm] = useState<IEspelhoReferencialForm | null>(null);
    const inputArquivoRef = useRef<HTMLInputElement>(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const result = await EspelhoReferencialService.listar(buscaAplicada, pagina * POR_PAGINA, POR_PAGINA);
            setItens(result.itens || []);
            setTotal(result.total || 0);
        } catch (e: any) {
            setItens([]);
            setTotal(0);
            Swal.fire({ text: e?.message || 'Erro ao carregar o espelho de referencial.', icon: 'error', timer: 5000, showConfirmButton: false });
        } finally {
            setLoading(false);
        }
    }, [buscaAplicada, pagina]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // Busca com debounce — volta para a primeira página a cada termo novo.
    useEffect(() => {
        const timer = setTimeout(() => {
            setPagina(0);
            setBuscaAplicada(busca.trim());
        }, 400);
        return () => clearTimeout(timer);
    }, [busca]);

    const enviarPlanilha = async (arquivo?: File) => {
        if (!arquivo) return;
        setEnviando(true);
        try {
            const r = await EspelhoReferencialService.upload(arquivo);
            const detalhes = [
                `${r.inseridas} conta(s) incluída(s)`,
                `${r.atualizadas} atualizada(s)`,
                `${r.ignoradasSemReferencial} ignorada(s) por não ter referencial`,
            ];
            if (r.duplicadasNoArquivo > 0) detalhes.push(`${r.duplicadasNoArquivo} repetida(s) na planilha (valeu a última)`);
            Swal.fire({ title: 'Planilha carregada', html: detalhes.join('<br/>'), icon: 'success' });
            setPagina(0);
            await carregar();
        } catch (e: any) {
            Swal.fire({ text: e?.message || 'Erro ao carregar a planilha.', icon: 'error' });
        } finally {
            setEnviando(false);
            if (inputArquivoRef.current) inputArquivoRef.current.value = '';
        }
    };

    const abrirNovo = () => {
        setEditando(null);
        setForm({ ...formVazio });
    };

    const abrirEdicao = (item: IEspelhoReferencial) => {
        setEditando(item);
        setForm({ conta: item.conta, nomeConta: item.nomeConta || '', referencial: item.referencial });
    };

    const fecharForm = () => {
        setForm(null);
        setEditando(null);
    };

    const salvar = async () => {
        if (!form) return;
        if (!form.conta.trim() || !form.referencial.trim()) {
            Swal.fire({ text: 'Informe a conta contábil e a conta referencial.', icon: 'warning', timer: 3000, showConfirmButton: false });
            return;
        }
        setSalvando(true);
        try {
            if (editando) {
                await EspelhoReferencialService.atualizar(editando.id, form);
            } else {
                await EspelhoReferencialService.criar(form);
            }
            fecharForm();
            await carregar();
        } catch (e: any) {
            Swal.fire({ text: e?.message || 'Erro ao salvar.', icon: 'error', timer: 5000, showConfirmButton: false });
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async (item: IEspelhoReferencial) => {
        const confirmacao = await Swal.fire({
            text: `Excluir a conta ${item.conta} do espelho de referencial?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Excluir',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#EF4444',
        });
        if (!confirmacao.isConfirmed) return;

        try {
            await EspelhoReferencialService.excluir(item.id);
            // Excluiu a última linha da página: volta uma para não ficar numa página vazia.
            if (itens.length === 1 && pagina > 0) setPagina(pagina - 1);
            else await carregar();
        } catch (e: any) {
            Swal.fire({ text: e?.message || 'Erro ao excluir.', icon: 'error', timer: 5000, showConfirmButton: false });
        }
    };

    const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

    return (
        <div className="card" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                    <div>
                        <h3 className="fw-bolder text-dark m-0" style={{ fontSize: '18px' }}>Configurar referencial</h3>
                        <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                            Espelho da conta contábil com a conta referencial. Planilha: coluna A = conta, C = nome, D = referencial —
                            só entram as contas com referencial preenchido, e reenviar atualiza as contas já cadastradas.
                        </div>
                    </div>
                    <button className="btn btn-sm btn-light" onClick={onVoltar}>&#x2190; Voltar ao Kanban</button>
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap mb-4">
                    <input
                        ref={inputArquivoRef}
                        type="file"
                        accept=".xls,.xlsx"
                        style={{ display: 'none' }}
                        onChange={e => enviarPlanilha(e.target.files?.[0])}
                    />
                    <button className="btn btn-sm" style={{ backgroundColor: '#00B7A1', color: '#fff', height: '38px' }}
                        disabled={enviando} onClick={() => inputArquivoRef.current?.click()}>
                        {enviando
                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Carregando...</>
                            : 'Importar planilha'}
                    </button>
                    <button className="btn btn-sm btn-light-primary" style={{ height: '38px' }} onClick={abrirNovo}>
                        Nova conta
                    </button>
                    <input
                        type="text"
                        className="form-control form-control-sm ms-auto"
                        style={{ height: '38px', maxWidth: '320px' }}
                        placeholder="Buscar por conta, nome ou referencial"
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                    />
                </div>

                <div className="table-responsive">
                    <table className="table table-row-bordered align-middle gs-3 gy-2 mb-0">
                        <thead>
                            <tr className="fw-bold text-muted" style={{ fontSize: '12px' }}>
                                <th style={{ width: '180px' }}>Conta</th>
                                <th>Nome</th>
                                <th style={{ width: '200px' }}>Referencial</th>
                                <th style={{ width: '110px' }} className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4} className="text-center text-muted py-5">Carregando...</td></tr>
                            ) : itens.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center text-muted py-5">
                                        {buscaAplicada
                                            ? 'Nenhuma conta encontrada para a busca.'
                                            : 'Nenhuma conta configurada. Importe a planilha ou inclua uma conta.'}
                                    </td>
                                </tr>
                            ) : itens.map(item => (
                                <tr key={item.id}>
                                    <td className="fw-bold text-dark">{item.conta}</td>
                                    <td>{item.nomeConta}</td>
                                    <td>{item.referencial}</td>
                                    <td className="text-end">
                                        <button className="btn btn-icon btn-sm btn-light me-1" title="Editar" onClick={() => abrirEdicao(item)}>
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                        <button className="btn btn-icon btn-sm btn-light-danger" title="Excluir" onClick={() => excluir(item)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                    <span className="text-muted" style={{ fontSize: '12px' }}>{total} conta(s)</span>
                    <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-sm btn-light" disabled={pagina === 0 || loading} onClick={() => setPagina(pagina - 1)}>Anterior</button>
                        <span className="text-muted" style={{ fontSize: '12px' }}>Página {pagina + 1} de {totalPaginas}</span>
                        <button className="btn btn-sm btn-light" disabled={pagina + 1 >= totalPaginas || loading} onClick={() => setPagina(pagina + 1)}>Próxima</button>
                    </div>
                </div>
            </div>

            {form && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.4)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(4px)',
                }} onClick={fecharForm}>
                    <div className="card shadow-lg" style={{ width: '480px', maxWidth: '92vw', borderRadius: '16px', border: 'none' }} onClick={e => e.stopPropagation()}>
                        <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: '#F8FAFC', borderRadius: '16px 16px 0 0' }}>
                            <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: '18px' }}>{editando ? 'Editar conta' : 'Nova conta'}</h3>
                            <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={fecharForm}>
                                <i className="bi bi-x fs-2"></i>
                            </button>
                        </div>
                        <div className="card-body p-5">
                            <div className="mb-4">
                                <label className="form-label fw-bold fs-7">Conta contábil</label>
                                <input type="text" className="form-control" maxLength={60} autoFocus
                                    value={form.conta} onChange={e => setForm({ ...form, conta: e.target.value })} />
                                <div className="text-muted mt-1" style={{ fontSize: '11px' }}>Os pontos são removidos ao salvar.</div>
                            </div>
                            <div className="mb-4">
                                <label className="form-label fw-bold fs-7">Nome da conta</label>
                                <input type="text" className="form-control" maxLength={255}
                                    value={form.nomeConta} onChange={e => setForm({ ...form, nomeConta: e.target.value })} />
                            </div>
                            <div>
                                <label className="form-label fw-bold fs-7">Conta referencial</label>
                                <input type="text" className="form-control" maxLength={50} placeholder="1.01.01.02.01"
                                    value={form.referencial} onChange={e => setForm({ ...form, referencial: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') salvar(); }} />
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2 py-4 px-5" style={{ borderRadius: '0 0 16px 16px' }}>
                            <button className="btn btn-sm btn-secondary px-4" onClick={fecharForm} disabled={salvando}>Cancelar</button>
                            <button className="btn btn-sm px-4" style={{ backgroundColor: '#00B7A1', color: '#fff' }} onClick={salvar} disabled={salvando}>
                                {salvando ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EspelhoReferencial;
